import {
  ApplicationRef,
  createComponent,
  EmbeddedViewRef,
  EventEmitter,
  Injectable,
  Injector,
  TemplateRef,
  Type,
  ViewContainerRef,
} from '@angular/core';
import { TemplateHost } from '../templating/template-host';
import { ComponentDescriptor } from './dynamics.model';
import { SeahorseTemplateDirective } from '../templating/sh-template.directive';

/**
 * Service providing dynamic component creation features.
 */
@Injectable({
  providedIn: 'root',
})
export class DynamicsService {
  constructor(private _injector: Injector, private _appRef: ApplicationRef) {}

  createComponent<TComponent>(
    descriptor: ComponentDescriptor<TComponent>,
    hostView?: ViewContainerRef
  ) {
    const componentRef = createComponent(descriptor.component, {
      environmentInjector: this._appRef.injector,
      elementInjector: descriptor.injector || this._injector,
      projectableNodes: descriptor.projectableNodes,
    });

    const content = componentRef.location.nativeElement as HTMLElement;

    if (descriptor.hostAttributes) {
      descriptor.hostAttributes.forEach((attr) =>
        content.setAttribute(attr, '')
      );
    }

    if (descriptor.props) {
      Object.assign(componentRef.instance as object, descriptor.props);
    }

    if (descriptor.events) {
      const events = descriptor.events as Record<
        string,
        ((v: unknown) => void) | undefined
      >;
      const instance = componentRef.instance as Record<string, unknown>;
      Object.keys(events).forEach((key) => {
        const handler = events[key];
        if (handler) {
          (instance[key] as EventEmitter<unknown>).subscribe(handler);
        }
      });
    }

    componentRef.changeDetectorRef.detectChanges();

    if (hostView) {
      hostView.insert(componentRef.hostView);
    } else {
      this._appRef.attachView(componentRef.hostView);
      componentRef.onDestroy(() => {
        this._appRef.detachView(componentRef.hostView);
      });
    }

    return componentRef;
  }

  renderHtml(html: HTMLElement, hostView: ViewContainerRef) {
    const container = hostView.element.nativeElement;

    if (container.nodeType === Node.COMMENT_NODE) {
      // If the container is a comment node, create a wrapper
      const wrapper = document.createElement('div');
      wrapper.appendChild(html);
      container.parentNode?.insertBefore(wrapper, container);
    } else {
      container.appendChild(html);
    }
  }

  renderTemplate(
    templateRef: TemplateRef<any>,
    hostView: ViewContainerRef,
    context?: any
  ): EmbeddedViewRef<any> {
    return hostView.createEmbeddedView(templateRef, context);
  }

  renderStandaloneTemplate<THost extends TemplateHost>(
    templateHost: Type<THost>,
    hostView: ViewContainerRef,
    context?: any
  ): EmbeddedViewRef<any> {
    const hostRef = this.createComponent({
      component: templateHost,
    });

    hostRef.changeDetectorRef.detectChanges();
    const templateRef = hostRef.instance.templateRef;

    if (!templateRef) {
      throw new Error('No templateRef found in the provided TemplateHost');
    }

    return hostView.createEmbeddedView(templateRef, context);
  }

  renderSeahorseTemplate(
    templateDirective: SeahorseTemplateDirective
  ): EmbeddedViewRef<any> | null {
    if (
      !templateDirective ||
      !templateDirective.templateRef ||
      !templateDirective.vcRef
    ) {
      return null;
    }

    return this.renderTemplate(
      templateDirective.templateRef,
      templateDirective.vcRef,
      templateDirective.context
    );
  }
}
